import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { FormikCheckbox } from 'src/components/shared/form/formik/FormikCheckbox'
import { FormikCheckboxSelect } from 'src/components/shared/form/formik/FormikCheckboxSelect'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { getGigsafeRoles, getGigsafeRolesLoading } from 'src/selectors/gigsafeUser'
import { getMarkets } from 'src/selectors/market'
import { humanizeSnakeCase } from 'src/utils/humanizeSnakeCase'
import type { InviteGigsafeUserFormValues } from './InviteGigsafeUserForm.interface'

const InviteGigsafeUserFieldsComponent = () => {
  const { values, setFieldValue } = useFormikContext<InviteGigsafeUserFormValues>()

  const roles = useSelector(getGigsafeRoles)
  const rolesLoading = useSelector(getGigsafeRolesLoading)
  const markets = useSelector(getMarkets)

  // `roleId` is `@IsUUID` on the backend, never a role name — the option value is `role.id`,
  // never `role.name`.
  const roleOptions = useMemo(() => {
    return roles.map((role) => {
      return { label: humanizeSnakeCase(role.name), value: role.id }
    })
  }, [roles])

  const marketOptions = useMemo(() => {
    return markets.map((market) => {
      return { label: market.title, value: market.id }
    })
  }, [markets])

  // Clears any prior selection when All Markets is switched back on, so a stale selection can
  // never be resubmitted after the checkbox is toggled off and on again.
  const handleAllMarketsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const allMarkets = event.target.checked

      setFieldValue('allMarkets', allMarkets)

      if (allMarkets) {
        setFieldValue('marketIds', [])
      }
    },
    [setFieldValue],
  )

  return (
    <div css={tw`flex flex-col gap-4`}>
      <FormikTextField<InviteGigsafeUserFormValues>
        name="email"
        label="Email *"
        data-testid="invite-gigsafe-user-email-input"
      />
      {/* First and last name are one fact split in two — they share a row rather than each
          taking a full-width line of their own. */}
      <div css={tw`grid grid-cols-1 gap-4 md:grid-cols-2`}>
        <FormikTextField<InviteGigsafeUserFormValues>
          name="firstName"
          label="First name *"
          data-testid="invite-gigsafe-user-first-name-input"
        />
        <FormikTextField<InviteGigsafeUserFormValues>
          name="lastName"
          label="Last name *"
          data-testid="invite-gigsafe-user-last-name-input"
        />
      </div>
      <FormikSelect<InviteGigsafeUserFormValues>
        name="roleId"
        label="Role *"
        options={roleOptions}
        disabled={rolesLoading}
        data-testid="invite-gigsafe-user-role-select"
      />
      <FormikCheckbox<InviteGigsafeUserFormValues>
        name="allMarkets"
        label="All markets"
        onChange={handleAllMarketsChange}
        inputProps={{ 'data-testid': 'invite-gigsafe-user-all-markets-checkbox' }}
      />
      {/* The list is only an input when it is actually being used — leaving a long disabled
          column of every market under a ticked "All markets" is noise, not information. */}
      {values.allMarkets ? null : (
        <FormikCheckboxSelect<InviteGigsafeUserFormValues>
          name="marketIds"
          label="Markets"
          options={marketOptions}
          dataTestId="invite-gigsafe-user-markets-select"
        />
      )}
    </div>
  )
}

export const InviteGigsafeUserFields = memo(InviteGigsafeUserFieldsComponent)
